import { EFeatures } from '@constants';
import { dateProvider } from '@utils';
import { Avatar, Button } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PendingTasksWrapper } from '../shared/WidgetShared.styles';

import {
  Wrapper,
  TitleWrapper,
  TitleText,
  MessageWrapper,
  SenderWrapper,
  NameWrapper,
  DateWrapper,
  ContentWrapper,
  WidgetChatWrapper,
  ButtonsWrapper
} from './ChatWidget.styles';

import { LoadingCircleSpinner } from '@/components/Loaders';
import { useTagFeatures } from '@/hooks';
import {
  useChatByParticipantsDetail,
  useCurrentStudentTutorDetail,
  useMutateChatMessage,
  useProfile,
  useStudentPendingChatMessages
} from '@/store';

const AVATAR_SIZE = 32;

export const ChatWidget: React.FC = () => {
  const { t } = useTranslation('StudentDashboardTranslations');
  const { canRender } = useTagFeatures();
  const {
    pendingMessages,
    isLoading,
    refetch: refetchPendingMessages
  } = useStudentPendingChatMessages();
  const { profile } = useProfile();
  const { tutor, isLoading: isLoadingTutor } = useCurrentStudentTutorDetail();
  const { chat } = useChatByParticipantsDetail({
    propTutorId: tutor?._id,
    propStudentId: profile?._id
  });
  const { setAllMessagesAsRead } = useMutateChatMessage();

  const navigateTo = useNavigate();

  const handleOpenChat = () => {
    if (!tutor || !profile) return;
    navigateTo(`/chat/${tutor._id}/${profile._id}`);
  };

  const handleMarkAsRead = () => {
    if (!chat) return;
    setAllMessagesAsRead(chat._id, {
      onSuccess: () => {
        refetchPendingMessages();
      }
    });
  };

  const lastMessage = useMemo(() => {
    if (!pendingMessages) return null;
    return pendingMessages.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )[0];
  }, [pendingMessages]);

  if (
    !canRender(EFeatures.TUTORING) ||
    (!isLoadingTutor && !tutor) ||
    pendingMessages?.length === 0
  )
    return null;

  return (
    <WidgetChatWrapper>
      <TitleWrapper>
        <TitleText>{t('CHAT_WIDGET_TITLE')}</TitleText>
        <PendingTasksWrapper>
          {t('PENDING_MESAGES', { count: pendingMessages?.length })}
        </PendingTasksWrapper>
      </TitleWrapper>
      <Wrapper>
        {isLoading && <LoadingCircleSpinner />}
        {lastMessage && (
          <MessageWrapper>
            <SenderWrapper>
              <Avatar size={AVATAR_SIZE} src={lastMessage.sender.avatar?.url}>
                {!lastMessage.sender.avatar?.url && lastMessage.sender.name[0]}
              </Avatar>
              <NameWrapper>{lastMessage.sender.name}</NameWrapper>
              <DateWrapper>
                {dateProvider.format(lastMessage.createdAt, 'dd/MM/yyyy HH:mm')}
              </DateWrapper>
            </SenderWrapper>
            <ContentWrapper dangerouslySetInnerHTML={{ __html: lastMessage.content ?? '' }} />
          </MessageWrapper>
        )}
        <ButtonsWrapper>
          <Button type="text" onClick={handleMarkAsRead}>
            {t('MARK_MESSAGE_AS_READ')}
          </Button>
          <Button type="primary" onClick={handleOpenChat}>
            {t('OPEN_CHAT')}
          </Button>
        </ButtonsWrapper>
      </Wrapper>
    </WidgetChatWrapper>
  );
};
